import {Trans} from '@lingui/macro';
import {isUndefined, noop} from 'lodash';
import React, {ComponentProps, FC, ReactNode} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupProps} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {Dialog} from './dialog';
import {DialogButtonGroup} from './dialogButtonGroup';
import {DialogContent} from './dialogContent';
import {DialogHeader, DialogHeaderProps} from './dialogHeader';

/*
 * Props.
 */

interface OkCancelDialogProps extends ComponentProps<typeof Dialog>, DialogHeaderProps, ButtonGroupProps {
  /** Renderer to customize the text of the Ok button. */
  okButtonLabel?: ReactNode;
  /** Renderer to customize the text of the Cancel button. */
  cancelButtonLabel?: ReactNode;
  /** Renderer to customize the icon in the title. */
  titleIcon?: ReactNode;
  /** The style of the "Ok" primary button. */
  okButtonVariant?: ButtonVariantsEnum;
  /** Event handler for the "Ok" primary button. */
  onOkClick?: Handler;
  /** Enable or disable the "Ok" primary button. */
  isOkDisabled?: boolean;
  /** Enable or disable the "Cancel" button. */
  isCancelDisabled?: boolean;
  /** Enable or disable capturing input for keyboard shortcuts */
  isCaptureInputsDisabled?: boolean;
  buttonGroupClassName?: string | null;
  isCancelHidden?: boolean;
}

/*
 * Constants.
 */

/*
 * Component.
 */

/** Customizable dialog with one action and one cancel button. */
export const OkCancelDialog: FC<OkCancelDialogProps> = (props) => {
  const {
    className,
    title,
    onOkClick,
    isOkDisabled,
    isCancelDisabled,
    buttonGroupClassName,
    isCancelHidden,
    titleIcon,
    okButtonVariant = ButtonVariantsEnum.PRIMARY,
  } = props;

  const confirmAction = props.isCaptureInputsDisabled
    ? KeyboardActionsEnum.CONFIRM_WITHOUT_INPUT_CAPTURE
    : KeyboardActionsEnum.CONFIRM;

  const keyHandlers = {
    [confirmAction]: isOkDisabled ? noop : onOkClick,
    [KeyboardActionsEnum.CLOSE]: props.onRequestClose ? props.onRequestClose : noop,
  };

  return (
    <Dialog {...props}>
      <KeyboardShortcuts handlers={keyHandlers}>
        <DialogHeader title={title} titleIcon={titleIcon} renderTooltip={props.renderTooltip} />
        <DialogContent>{props.children}</DialogContent>
        <DialogButtonGroup
          {...props}
          className={computeButtonGroupClassName(className, buttonGroupClassName)}
        >
          <ButtonGroupSpacer />
          {!isCancelHidden && (
            <Button onClick={props.onRequestClose} isDisabled={isCancelDisabled}>
              {props.cancelButtonLabel || <Trans id="dialog-cancel">Cancel</Trans>}
            </Button>
          )}
          <Button variant={okButtonVariant} onClick={onOkClick} isDisabled={isOkDisabled}>
            {props.okButtonLabel || <Trans id="dialog-ok">Ok</Trans>}
          </Button>
        </DialogButtonGroup>
      </KeyboardShortcuts>
    </Dialog>
  );
};

/*
 * Helpers.
 */

const computeButtonGroupClassName = (className?: string, buttonGroupClassName?: string | null) => {
  if (isUndefined(buttonGroupClassName)) {
    return className;
  }
  // If we are passing in null, we should not have any className passed down
  if (buttonGroupClassName === null) {
    return undefined;
  }
  return buttonGroupClassName;
};
